// src/Dashboard/pages/user/Inscription/components/TrialValidationDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaSave, FaThumbsUp, FaUserMinus } from "react-icons/fa";
import { toast } from "react-toastify";

import DrawerShell from "./DrawerShell";

import {
  getAdherentsByUser,
  removeAdherentFromGroup,
  updateAdherent,
  updateAdherentStatus,
} from "../../../../../services/adherentServices";
import { getGroupName, getGroupsList } from "../../../../../services/groupServices";
import { sendNotificationToAdmin } from "../../../../../services/notificationServices";

const NON_VALID_STATUS = "Non Licencié - Arrêt";
const NON_VALID_STATUS_ASCII = "Non Licencie - Arret";

export default function TrialValidationDrawer({
  isOpen,
  onClose,
  adherent,
  user,
}) {
  const [decision, setDecision] = useState("valide");
  const [saving, setSaving] = useState(false);
  const [groupIds, setGroupIds] = useState([]);
  const [groupNames, setGroupNames] = useState([]);

  useEffect(() => {
    if (!isOpen || !adherent?.id) return;

    let cancelled = false;

    const resolveGroups = async () => {
      try {
        setDecision("valide");

        const allGroups = await getGroupsList().catch(() => []);
        const names = await getGroupName(adherent.id).catch(() => []);

        if (cancelled) return;

        const cleanNames = Array.isArray(names) ? names : [];
        setGroupNames(cleanNames);

        const ids = cleanNames
          .map((name) => {
            const found = allGroups.find(
              (group) => (group.nom || group.name) === name
            );

            return found?.id || null;
          })
          .filter(Boolean);

        setGroupIds([...new Set(ids)]);
      } catch (error) {
        console.error(error);
        setGroupIds([]);
        setGroupNames([]);
      }
    };

    resolveGroups();

    return () => {
      cancelled = true;
    };
  }, [adherent?.id, isOpen]);

  const groupLabel = useMemo(() => {
    return groupNames.length ? groupNames.join(", ") : "—";
  }, [groupNames]);

  const readCurrentStatus = async () => {
    try {
      const list = await getAdherentsByUser(user?.userId);
      const found = Array.isArray(list)
        ? list.find((item) => item.id === adherent.id)
        : null;

      return found?.statut_inscription || null;
    } catch {
      return null;
    }
  };

  const tryUpdateStatus = async (status) => {
    try {
      await updateAdherent(adherent.id, {
        statut_inscription: status,
      });

      return true;
    } catch (error) {
      console.warn("updateAdherent statut failed:", error);
      return false;
    }
  };

  const ensureStatus = async (wanted) => {
    await tryUpdateStatus(wanted);

    let current = await readCurrentStatus();

    if (current === wanted || current === NON_VALID_STATUS_ASCII) {
      return true;
    }

    try {
      await updateAdherent(adherent.id, {
        statut_inscription: NON_VALID_STATUS_ASCII,
      });
    } catch {}

    try {
      await updateAdherentStatus(adherent.id, wanted);
    } catch {}

    current = await readCurrentStatus();

    return current === wanted || current === NON_VALID_STATUS_ASCII;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!adherent?.id) {
      toast.error("Adhérent introuvable.");
      return;
    }

    try {
      setSaving(true);

      if (decision === "valide") {
        const ok = await ensureStatus("Inscrit");

        if (!ok) {
          toast.error("Impossible de mettre à jour le statut en Inscrit.");
          onClose?.(false);
          return;
        }

        const message = `${user?.prenom || ""} ${user?.nom || ""} a validé le cours d’essai de ${adherent.prenom} ${adherent.nom} sur le groupe ${groupLabel}.`;
        await sendNotificationToAdmin(message, user?.token);

        toast.success("Cours d’essai validé.");
        onClose?.(true);
        return;
      }

      for (const groupId of groupIds) {
        try {
          await removeAdherentFromGroup(adherent.id, groupId);
        } catch (error) {
          console.warn("removeAdherentFromGroup failed:", groupId, error);
        }
      }

      const ok = await ensureStatus(NON_VALID_STATUS);

      if (!ok) {
        toast.error("Impossible de mettre à jour le statut.");
        onClose?.(false);
        return;
      }

      const message = `${user?.prenom || ""} ${user?.nom || ""} n'a pas validé le cours d’essai de ${adherent.prenom} ${adherent.nom} sur le groupe ${groupLabel}. Place libérée.`;
      await sendNotificationToAdmin(message, user?.token);

      toast.success("Cours d’essai non validé. Place libérée.");
      onClose?.(true);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la validation du cours d’essai.");
      onClose?.(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={() => onClose?.(false)}
      title="Validation du cours d’essai"
      subtitle={
        adherent ? `${adherent.prenom} ${adherent.nom}` : "Décision du cours d’essai"
      }
      icon={<FaThumbsUp />}
      width="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onClose?.(false)}
            disabled={saving}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Annuler
          </button>

          <button
            type="submit"
            form="trial-form"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-violet-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-violet-700 disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      }
    >
      {!adherent ? null : (
        <form id="trial-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/60">
            <p className="text-sm leading-6 text-slate-700 dark:text-slate-300">
              Décision pour{" "}
              <span className="font-bold">
                {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
              </span>
              .
            </p>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Groupe concerné : <span className="font-bold">{groupLabel}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label
              className={`cursor-pointer rounded-[24px] border p-5 transition ${
                decision === "valide"
                  ? "border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                  : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-900"
              }`}
            >
              <input
                type="radio"
                name="decision"
                value="valide"
                checked={decision === "valide"}
                onChange={() => setDecision("valide")}
                className="sr-only"
              />

              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600 text-white">
                  <FaCheck />
                </span>

                <div>
                  <p className="font-bold text-slate-900 dark:text-white">
                    Essai validé
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Le statut passera à Inscrit.
                  </p>
                </div>
              </div>
            </label>

            <label
              className={`cursor-pointer rounded-[24px] border p-5 transition ${
                decision === "non"
                  ? "border-rose-300 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10"
                  : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-900"
              }`}
            >
              <input
                type="radio"
                name="decision"
                value="non"
                checked={decision === "non"}
                onChange={() => setDecision("non")}
                className="sr-only"
              />

              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-600 text-white">
                  <FaUserMinus />
                </span>

                <div>
                  <p className="font-bold text-slate-900 dark:text-white">
                    Essai non validé
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    La place sera libérée dans le groupe.
                  </p>
                </div>
              </div>
            </label>
          </div>
        </form>
      )}
    </DrawerShell>
  );
}